import { combineReducers, configureStore, type Action } from "@reduxjs/toolkit";
import workspaceReducer from "./slices/workspaceSlice";
import workspaceDetailsReducer from "./slices/workspaceDetailsSlice";
import groupReducer from "./slices/groupSlice";
import linkReducer from "./slices/linkSlice";
import reminderReducer from "./slices/reminderSlice";
import searchReducer from "./slices/searchSlice";
import threadReducer from "./slices/threadSlice";
import cacheReducer from "./slices/cacheSlice";
import settingsReducer from "./slices/settingsSlice";
import { userChanged } from "./sessionActions";
import { adoptSignedInUser, clearUserData } from "../auth/userSession";
import { session } from "../auth/session";
import { currentSubject } from "../utils/userScope";
import { bannerStore } from "../utils/bannerStore";

export { userChanged } from "./sessionActions";

const appReducer = combineReducers({
  workspace: workspaceReducer,
  workspaceDetails: workspaceDetailsReducer,
  groups: groupReducer,
  links: linkReducer,
  reminder: reminderReducer,
  search: searchReducer,
  thread: threadReducer,
  cache: cacheReducer,
  settings: settingsReducer,
});

type AppState = ReturnType<typeof appReducer>;

/**
 * The slices that hold the signed-in user's data. On a user change each goes
 * back to its initial state; the data slices' initial state is computed
 * lazily, so it re-hydrates from the new user's cache (or comes up empty).
 * settings is about the browser, not the user (the mobile view is
 * kast-common's useMobileView, in localStorage).
 */
export const USER_DATA_SLICES = [
  "workspace",
  "workspaceDetails",
  "groups",
  "links",
  "reminder",
  "search",
  "thread",
  "cache",
] as const satisfies readonly (keyof AppState)[];

export function rootReducer(state: AppState | undefined, action: Action) {
  if (state && userChanged.match(action)) {
    const next: Partial<AppState> = { ...state };
    for (const slice of USER_DATA_SLICES) delete next[slice];
    return appReducer(next as AppState, action);
  }
  return appReducer(state, action);
}

// The stored data belongs to whoever the session says is signed in. Before
// the store exists (the data slices hydrate from the cache as it is created),
// point the cache at that user, clearing another user's data first.
let subject = currentSubject();
adoptSignedInUser(subject);

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST"],
      },
    }),
});

// On every session change: when the user changes
// (a sign-in, a different account, or the session ending: sign-out, a
// rejected refresh, a 401 with nothing to refresh) swap the cache to the new
// user or clear it, then reset the store's data slices. A token refresh keeps
// the same `sub`, so it leaves the data alone.
//
// A sign-out that leaves the page (signOutAndLeave) publishes its signed-out
// state with `leaving` set: the user's data is cleared here all the same
// (`useSession` keeps showing the page as it was until it's gone, so it doesn't
// flash signed out on the way to the login site).
session.subscribe(() => {
  const state = session.getState();
  if (state.leaving) {
    subject = null;
    clearUserData();
    return;
  }
  const next = currentSubject();
  if (next === subject) return;
  subject = next;
  if (next) adoptSignedInUser(next);
  else clearUserData();
  // The previous user's banners and their history go with their data.
  bannerStore.reset();
  store.dispatch(userChanged());
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
